import { getAuthUserInfo } from '@/services/user';
import { COOKIE_MAX_AGE, I18nLanguage, LANGUAGE_COOKIE_KEY, SUPPORTED_I18N_LANGUAGES } from './const';

/**
 * 判断语言是否在受支持列表中
 */
export function isSupportedLanguage(lang?: string | null): lang is I18nLanguage {
  return !!lang && SUPPORTED_I18N_LANGUAGES.has(lang);
}

/**
 * 拉取已登录用户的语言设置，
 * 用户语言 -> IP 语言 -> 本地缓存 -> 默认英语
 * 都不可用时返回 null
 */
export async function fetchAuthedLanguage(): Promise<string | null> {
  try {
    const { data } = await getAuthUserInfo();
    if (isSupportedLanguage(data.language)) {
      return data.language;
    }
    if (isSupportedLanguage(data.ipLanguage)) {
      return data.ipLanguage;
    }
  } catch {
    // 拉取失败回退到本地缓存
  }
  return null;
}

/**
 * 设置语言到 Cookie
 */
export function setLanguageSetting(language: string) {
  document.cookie = `${LANGUAGE_COOKIE_KEY}=${language};max-age=${COOKIE_MAX_AGE};path=/`;
}

/**
 * 从 Cookie 获取语言设置
 */
export function getLanguageSetting(): string | null {
  const cookieValue = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${LANGUAGE_COOKIE_KEY}=`));

  if (cookieValue) {
    return cookieValue.split('=')[1] as I18nLanguage;
  }

  const { isSupported, browserLanguage } = getNormalizedBrowserLanguage();
  return isSupported ? browserLanguage : null;
}

/**
 * 获取标准化的浏览器语言代码
 */
export function getNormalizedBrowserLanguage(): {
  browserLanguage: I18nLanguage;
  isSupported: boolean;
} {
  // 获取浏览器语言设置
  const browserLang = navigator.language.toLowerCase();
  // 获取浏览器语言前半部分，我们支持的是en，但是浏览navigator.language是en-**
  const esLang = browserLang.split('-')[0];
  const supportedLanguages = Object.values(I18nLanguage);

  // 判断是否支持该语言的国际化
  let isSupported = false;
  let normalizedLang = browserLang;

  for (const supLng of supportedLanguages) {
    // 只要满足browserLang或者浏览器语言前缀在支持的语言中，就说明项目支持这个语言的国际化
    if (supLng === browserLang || supLng === esLang) {
      normalizedLang = supLng;
      isSupported = true;
      break;
    }
  }

  return {
    browserLanguage: normalizedLang as I18nLanguage,
    isSupported,
  };
}
